import type { UserAvailability } from '@sfa/shared';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    /**
     * The parsed error body, when the response carried JSON.
     *
     * Several API errors are **structured refusals** rather than failures: the
     * succession 409 (PAC-91 §7) hands back the household and the members
     * eligible to take over, and the ambiguous-household 409 (PAC-91 §5) hands
     * back the candidate households. In both, the body is what the UI needs to
     * offer the user a way forward — a message alone leaves them at a dead end.
     *
     * Narrow it with a `code` check, never by matching the message: the message
     * is written for a human and will be reworded.
     */
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The current access token, or null when signed out.
 *
 * Exported for the notification stream (PAC-154), whose `fetch` override reads
 * it on every (re)connect — a header captured once would be the expired token
 * forever. Everything else goes through {@link apiFetch}.
 */
/** Where `push-subscription.ts` records which user this browser is subscribed for. */
export const PUSH_OWNER_STORAGE_KEY = 'push:ownerUserId';

export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

/**
 * When the stored access token expires, as epoch milliseconds — or null when
 * there is no token or it carries no `exp`. Read from the JWT's payload
 * without verifying it: the browser cannot verify, and does not need to; this
 * only decides whether to refresh *before* a long-lived request (the
 * notification stream) rather than be refused mid-way (PAC-154 PR2).
 */
export function getAccessTokenExpiry(): number | null {
  const token = getAccessToken();
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const json = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: unknown };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function getRefreshToken(): string | null {
  return localStorage.getItem('refreshToken');
}

export function setTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
}

/**
 * Device preferences that survive logout.
 *
 * Everything else in `localStorage` is session-derived and must not — see
 * {@link clearTokens}. These are the opposite: they describe the *browser*, not
 * the person signed into it, they contain nothing about the account, and
 * resetting them is a bug rather than a safeguard. Logging out used to repaint
 * the app dark for anyone who had chosen the light theme.
 *
 * Keep this list short, and only add a key when leaking it to the next user of
 * the same machine would be harmless.
 */
const PRESERVED_UI_PREFERENCE_KEYS = [
  'theme',
  'sidebar:collapsed',
  // Whose push subscription this browser holds (`push-subscription.ts`). It
  // describes the *browser* — the subscription outlives the session — and
  // wiping it is what made push turn itself off for the same user signing
  // back in after an expired refresh token (PAC-154 PR4 review). An opaque
  // ObjectId; the next person at the machine learns nothing from it.
  PUSH_OWNER_STORAGE_KEY,
] as const;

/**
 * Key prefixes preserved alongside {@link PRESERVED_UI_PREFERENCE_KEYS}, for
 * entries whose exact key is not known ahead of time.
 *
 * Today: the white-label branding cache, keyed `tenant:<host>`. It describes
 * the **host**, not the person signed into it — it holds an agency's public
 * name and logo URL, which is exactly what the next visitor to that address
 * sees on the login page anyway. Wiping it on logout would make the very next
 * paint flash "AgencyOps" at someone who has never seen that name.
 */
const PRESERVED_UI_PREFERENCE_PREFIXES = ['tenant:'] as const;

/**
 * Wipe every trace of the session from the browser. Clears the whole
 * localStorage and sessionStorage rather than individual keys so no cached
 * data (tokens, user, branch selection, or anything added later) can leak
 * into the next session — then restores the handful of device preferences in
 * {@link PRESERVED_UI_PREFERENCE_KEYS}.
 *
 * Deliberately still a clear-then-restore rather than a list of keys to remove:
 * the default for anything new stays "wiped", which is the safe direction.
 */
export function clearTokens() {
  try {
    const prefixed = Object.keys(localStorage).filter((key) =>
      PRESERVED_UI_PREFERENCE_PREFIXES.some((prefix) => key.startsWith(prefix)),
    );
    const preserved = [...PRESERVED_UI_PREFERENCE_KEYS, ...prefixed].map(
      (key) => [key, localStorage.getItem(key)] as const,
    );

    localStorage.clear();
    sessionStorage.clear();

    for (const [key, value] of preserved) {
      if (value !== null) localStorage.setItem(key, value);
    }
  } catch {
    // Storage may be unavailable (private mode / SSR); ignore.
  }
}

export interface AuthUser {
  id: string;
  email: string;
  /** Full name from firstName/lastName, or null if not set. */
  name: string | null;
  /** The raw name halves, for the profile form (PAC-81). */
  firstName: string | null;
  lastName: string | null;
  /**
   * Relative API path of the profile photo, or null when none is set (PAC-81).
   * Stable (it carries a cache-buster, not a signature), but **authenticated**
   * — fetch it with {@link apiFetchBlob}, never point an `<img src>` at it.
   */
  avatarUrl: string | null;
  /**
   * Taking new leads or not (PAC-139 §6). The user's own switch, set from the
   * sidebar (`AvailabilityToggle`) — nothing computes it.
   */
  availability: UserAvailability;
  /** Human-readable role names (e.g. ["Owner"]). For display only. */
  roles: string[];
  agencyId: string | null;
  branchId: string | null;
  permissions: string[];
  scope: string;
  dataScope: string;
  isPlatformAdmin: boolean;
  /**
   * The agency's IANA zone (PAC-159) — what "today" means for a sold, quote
   * or lead date. Feed it to `agencyTodayYmd` (`@/lib/dates`); never default a
   * business date from the browser's clock.
   *
   * Optional only because a blob stored before the field existed deserializes
   * without it; `agencyTodayYmd` falls back to the browser's day until the
   * next `/auth/me` refresh rewrites the blob.
   */
  timeZone?: string;
  /**
   * Whether this user still owes their agency its first-run setup (PAC-69).
   *
   * True only for the owner of an agency onboarded through the Super Admin
   * panel, and only until they finish or skip it. Drives `RoleLanding`'s
   * redirect to `/welcome/agency`.
   */
  agencySetupPending: boolean;
  /**
   * The operator's user id when this session was minted by impersonation
   * (PAC-70), else `null`. Provenance only — the API never reads it back, and
   * the UI shows no banner by product decision; it is here so the stored blob
   * matches what `/auth/me` returns.
   */
  impersonatedBy?: string | null;
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem('user');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function setStoredUser(user: AuthUser) {
  localStorage.setItem('user', JSON.stringify(user));
}

/**
 * The one refresh in flight, shared by every caller that hits a 401 at the
 * same moment.
 *
 * The API rotates the refresh token on every use, so two concurrent refreshes
 * with the same token are a race the second one loses — and it then clears the
 * session for a 401 that was not a real sign-out. Two concurrent 401s were
 * already possible (several queries refetching on focus); the notification
 * stream's reconnect at the token's `exp` made it routine (PAC-154 PR2).
 */
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Exchange the refresh token for a new pair. Single-flight: concurrent callers
 * await the same request and get the same answer. Clears the session when the
 * refresh is refused, so a caller that gets `null` back is signed out.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function performRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!res.ok) {
    clearTokens();
    return null;
  }

  const data = (await res.json()) as {
    accessToken: string;
    refreshToken: string;
    user: AuthUser;
  };
  setTokens(data.accessToken, data.refreshToken);
  setStoredUser(data.user);
  return data.accessToken;
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }

  let token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const branchId = localStorage.getItem('branchId');
  if (branchId) {
    headers.set('X-Branch-Id', branchId);
  }

  let res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401 && getRefreshToken()) {
    token = await refreshAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    }
  }

  if (!res.ok) {
    const text = await res.text();
    let message = text || res.statusText;
    let body: unknown;
    try {
      const json = JSON.parse(text) as { message?: string | string[] };
      body = json;
      if (json.message) {
        message = Array.isArray(json.message) ? json.message.join(', ') : json.message;
      }
    } catch {
      // use raw text
    }
    throw new ApiError(message, res.status, body);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

/**
 * `apiFetch` for binary responses — same auth header and 401-refresh-retry,
 * but resolves to a `Blob` instead of parsing JSON.
 *
 * Exists for the profile photo (PAC-81): `GET /me/avatar` is authenticated,
 * and an `<img src>` cannot send an `Authorization` header — so the bytes are
 * fetched here and rendered through an object URL instead.
 */
export async function apiFetchBlob(path: string): Promise<Blob> {
  const headers = new Headers();
  let token = getAccessToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let res = await fetch(`${API_BASE}${path}`, { headers });

  if (res.status === 401 && getRefreshToken()) {
    token = await refreshAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      res = await fetch(`${API_BASE}${path}`, { headers });
    }
  }

  if (!res.ok) {
    throw new ApiError(res.statusText, res.status);
  }

  return res.blob();
}

/**
 * Fetch for `@Public()` API routes. No `Authorization`, no `X-Branch-Id`, and
 * no 401-refresh — same `ApiError` contract otherwise.
 *
 * Deliberately not `apiFetch`. A producer previewing their own share link is a
 * completely ordinary thing to do, and if their access token happens to be
 * expired, `apiFetch` would attempt a refresh, fail it, and call
 * `clearTokens()` — which wipes localStorage and **logs them out of the app for
 * opening their own link**. Precedent: `uploadToPresignedUrl` in
 * `deal-audits-api.ts` bypasses `apiFetch` for the same class of reason.
 */
export async function publicFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    const text = await res.text();
    let message = text || res.statusText;
    let body: unknown;
    try {
      const json = JSON.parse(text) as { message?: string | string[] };
      body = json;
      if (json.message) {
        message = Array.isArray(json.message) ? json.message.join(', ') : json.message;
      }
    } catch {
      // use raw text
    }
    throw new ApiError(message, res.status, body);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export async function login(email: string, password: string) {
  const data = await apiFetch<{
    accessToken: string;
    refreshToken: string;
    user: AuthUser;
  }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  setTokens(data.accessToken, data.refreshToken);
  setStoredUser(data.user);
  return data;
}

export { API_BASE };

/**
 * The caller's identity and *current* permissions, straight from the API.
 *
 * The stored `user` blob is only rewritten at login, token refresh and
 * accept-invite, so without this a permission change did not reach a signed-in
 * browser until the access token refreshed — the API enforced it immediately
 * while the UI kept offering actions that had started 403ing.
 *
 * Re-stores the blob so a reload picks up the fresh set too.
 */
export async function fetchMe(): Promise<AuthUser> {
  const user = await apiFetch<AuthUser>('/auth/me');
  setStoredUser(user);
  return user;
}
